import { LineItem, Note } from "@/models/order/order";
import { NoteFields } from "@/models/order/helpers";
import { UmbrellaType } from "@/models/products/umbrella";
import { SharedConfigLine } from "@/models/configurator/shared_config";
import { ProductType } from "@/models/products/product";
import { BaseModel } from "@/models/products/base";
import { Finish } from "@/models/products/finish";
import { Stem } from "@/models/configurator/configurator_options";
import {
  BaseSelection,
  BaseSelectionOptions,
  SPIGOT_STEM
} from "@/models/configurator/base_selection";
import {
  baseDesignKey,
  judgeSharedDesign,
  markSharedDesignAdjusted,
  stampSharedDesign,
  stampSharedDesignOrigin
} from "./shared_design";
import { stampSetup } from "./line_setup";

/**
 * FUPOF-193: the non-umbrella cart lines an umbrella page adds alongside its
 * umbrella — the base, and any top cover.
 *
 * These were once added by visiting CustomizeBase in sequence, then by being
 * constructed here from a shared design's payload without the rep seeing them.
 * Now the rep configures the base on the umbrella page itself, so this file no
 * longer decides anything: `BaseSelector` owns the selection and its pricing,
 * and what is left here is the translation either side of it —
 *
 *   resolveSharedBaseSelection : a shared design's codes -> a BaseSelection
 *                                the card can start from
 *   buildBaseLine / buildTopCoverLine
 *                              : a BaseSelection -> cart lines
 *
 * The two builders are pure. Price and SKU are passed in, because the card has
 * already priced the selection and the umbrella page gets the base SKU free
 * from its own /products/sku call.
 */

/** The bits of the configurator pages the resolver borrows. */
export interface SharedLineDeps {
  /** Items.getItem — resolves a model code to its catalog document. */
  getItem: (model: string) => Promise<any>;
}

export interface ResolvedBaseSelection {
  selection: BaseSelection | null;
  /** Set when the design named a base this catalog can no longer offer. */
  warning?: string;
}

/**
 * The options a base carries once it leaves the card: wheels only.
 *
 * `topCover` is a pairing the card holds so it can show a cover beside its
 * base, but in Shade Quote a cover is its own cart line — which is also how the
 * resolve endpoint hands a shared design over, with `baseOptions: {}` and the
 * cover promoted to an accessory line. Letting it through has two concrete
 * consequences, both verified live:
 *
 * - `POST /prices/base` returns **500** (`Cannot read properties of undefined
 *   (reading 'finishes')`) on any payload carrying a `topCover`, and the
 *   resulting "Unable to get price data" toast is sticky. That fires not just
 *   here but whenever the rep reopens the base line in CustomizeBase, which
 *   spreads stored `baseOptions` straight into the pricing call.
 * - `POST /products/sku` accepts it but answers with a *combined* SKU
 *   (`"36G-SQx3+W & 36G-SQ-TC-BZ"`), so the base line would take the cover's
 *   identity on top of its own while the cover line still carries it too.
 */
export function baseOptionsForLine(
  selection: BaseSelection
): BaseSelectionOptions {
  return selection.baseOptions.wheels ? { wheels: true } : {};
}

export interface CompanionLineOpts {
  multiplier: number;
  qty: number;
  /** The Shade Designer code these lines came from, if any. */
  shareId?: string | null;
  /** They no longer match that design (see shared_design.sharedDesignAdjusted). */
  sdAdjusted?: boolean;
  /**
   * The design's own key for this line (see shared_design.SharedDesignOrigin).
   * When given, the line is stamped with it and `sdAdjusted` is ignored: the
   * line is judged against it instead, so it can read as designed again later.
   */
  sdOrigin?: string | null;
  /**
   * The umbrella setup these lines join (see utility/line_setup). Required:
   * a base or cover built on the umbrella page always belongs to that page's
   * umbrella, and a line built without one could never be found again by the
   * setup editor — which would then add a second base beside it.
   */
  setupId: string;
}

/**
 * Split a shared top-cover line ("36G-SQ-TC-BZ") back into the catalog model
 * and finish the card holds separately.
 *
 * The finish is matched against the base's own `-TC` accessory rather than
 * trusted from the string, so a code naming a finish the cover no longer
 * offers resolves to no cover instead of an unpriceable one.
 */
function resolveSharedTopCover(
  topCoverLine: SharedConfigLine | null,
  baseModel: BaseModel
) {
  if (!topCoverLine?.model) return null;
  const lastHyphen = topCoverLine.model.lastIndexOf("-");
  if (lastHyphen === -1) return null;
  const coverModel = topCoverLine.model.substring(0, lastHyphen);
  const finishHandle = topCoverLine.model
    .substring(lastHyphen + 1)
    .toLowerCase();

  const accessory = (baseModel.accessories ?? []).find(
    a => a.model === coverModel
  );
  const finish = (accessory?.finishes ?? []).find(
    f => String(f.handle) === finishHandle
  );
  if (!accessory || !finish) return null;

  return {
    model: accessory.model,
    display_name: accessory.display_name,
    finish
  };
}

/**
 * A shared design's base, as codes, turned into the populated selection the
 * card starts from.
 *
 * The resolve endpoint hands over `{ base: "36G-SQx3", baseFinish: "gs",
 * baseStem: "Spigot", baseOptions: {} }` plus, as a separate line, a top cover
 * whose model already carries its finish ("36G-SQ-TC-BZ"). The card works in
 * documents, so the model code is fetched and the finish handle matched against
 * it here.
 */
export async function resolveSharedBaseSelection(
  baseLine: SharedConfigLine | null,
  topCoverLine: SharedConfigLine | null,
  deps: SharedLineDeps
): Promise<ResolvedBaseSelection> {
  const config = baseLine?.config as any;
  if (!baseLine || !config || !config.base) return { selection: null };

  const baseModel = (await deps.getItem(config.base)) as BaseModel;
  if (!baseModel || !baseModel.model) {
    return {
      selection: null,
      warning: `Base "${config.base}" is no longer available and was not added.`
    };
  }

  // A stem sold as a standalone base carries no finish, so fall back to the
  // model's first finish exactly as CustomizeBase.setDefaults() does. Never
  // leave baseFinish undefined: the cart's hasStructuredBase requires it to be
  // an object, and a line that fails that check is read as SKU-reconstructed
  // and wears a false "Added in NetSuite" pill.
  const finishes = baseModel.finishes || [];
  const baseFinish: Finish =
    finishes.find(f => f.handle === config.baseFinish) ??
    finishes[0] ??
    (({ handle: null, display_name: null } as unknown) as Finish);

  const baseOptions: BaseSelectionOptions = {};
  if (config.baseOptions?.wheels) baseOptions.wheels = true;

  const topCover = resolveSharedTopCover(topCoverLine, baseModel);
  if (topCover) baseOptions.topCover = topCover;

  return {
    selection: {
      base: baseModel,
      baseFinish,
      baseStem: (config.baseStem as Stem | typeof SPIGOT_STEM | null) ?? null,
      baseOptions
    }
  };
}

/**
 * The two fixed stem notes CustomizeBase writes, reproduced here so a base
 * added from the umbrella page reaches production saying the same thing.
 *
 * `addNotesToQuote` (CustomizeBase.vue:676-683) prepends one of these to the
 * rep's own stem note: "Spigot" means no stem was ordered at all, and a real
 * stem on a spigot-capable base ships with a beauty plate. Neither fact is
 * recoverable from the SKU or the price, so a line without them is a line the
 * factory reads differently — and a rep who adds the same base from the base
 * page gets a line that differs from this one in production notes alone.
 *
 * The card has no free-text note box, so the fixed string is the whole content.
 * Reopening the line in CustomizeBase round-trips cleanly: `setNotes()` strips
 * these before showing the box, and `addNotesToQuote()` puts them back.
 */
export const SPIGOT_NOTE = "Intended for Spigot use - No stem requested.";
export const BEAUTY_PLATE_NOTE = "Beauty plate included.";

/**
 * Whether this base can be spigot-mounted — CustomizeBase's
 * `needsSpigotOption`, keyed on the **base's own** product lines rather than
 * the umbrella's type, so both pages classify the same base the same way.
 *
 * That is deliberately not the rule BaseSelector uses to decide which stems to
 * *offer*: the card narrows its options to the umbrella in hand, while the note
 * describes the base as built.
 */
function baseAcceptsSpigot(base: BaseModel): boolean {
  return (base.product_lines ?? []).some(
    pl => pl.umbrella_type === UmbrellaType.Cantilever || pl.display_name === "Nova"
  );
}

/** The stem note a selection implies, or nothing. */
export function baseStemNotes(selection: BaseSelection): Note[] {
  if (selection.baseStem === SPIGOT_STEM) {
    return [{ field: NoteFields.Stem, content: SPIGOT_NOTE }];
  }
  if (selection.baseStem && baseAcceptsSpigot(selection.base)) {
    return [{ field: NoteFields.Stem, content: BEAUTY_PLATE_NOTE }];
  }
  return [];
}

/** The Shade Designer code, origin and adjusted flag a companion line takes. */
function stampCompanionDesign(line: LineItem, opts: CompanionLineOpts): void {
  if (!opts.shareId) return;
  stampSharedDesign(line, opts.shareId);
  if (opts.sdOrigin != null) {
    stampSharedDesignOrigin(line, { config: opts.sdOrigin });
    judgeSharedDesign(line);
  } else if (opts.sdAdjusted) {
    markSharedDesignAdjusted(line);
  }
}

/** The base line's design key for a selection (shared_design.designKey). */
export function baseLineDesignKey(selection: BaseSelection): string {
  return baseDesignKey({
    base: selection.base,
    baseFinish: selection.baseFinish,
    baseStem: selection.baseStem ?? null,
    baseOptions: baseOptionsForLine(selection)
  });
}

/** The top cover line's model, which is also its design key; null if none. */
export function topCoverLineModel(selection: BaseSelection): string | null {
  const cover = selection.baseOptions.topCover;
  return cover
    ? `${cover.model}-${String(cover.finish.handle).toUpperCase()}`
    : null;
}

/**
 * The base as a cart line.
 *
 * `product_line` carries the **model**, not the umbrella's product line. That
 * is this app's convention for base lines — OrderMixin.editItem routes a base
 * edit by model, and AddToQuoteFooter writes the model there when the base page
 * submits — so anything else makes a line that reopens onto an empty page.
 */
export function buildBaseLine(
  selection: BaseSelection,
  sku: string,
  unitPrice: number,
  opts: CompanionLineOpts
): LineItem {
  const line = ({
    product_type: ProductType.Base,
    display_name: selection.base.display_name,
    product_line: selection.base.model,
    model: selection.base.model,
    sku,
    qty: opts.qty,
    unit_price: unitPrice,
    multiplier: opts.multiplier,
    notes: "",
    item_details: {
      // The hydrated shape, deliberately: `baseFinish` must be the Finish
      // object or the cart hides the Details tooltip and mislabels the line.
      base: {
        base: selection.base,
        baseFinish: selection.baseFinish,
        baseStem: selection.baseStem ?? null,
        baseOptions: baseOptionsForLine(selection)
      },
      notes: baseStemNotes(selection)
    }
  } as unknown) as LineItem;
  stampCompanionDesign(line, opts);
  stampSetup(line, opts.setupId);
  return line;
}

/**
 * The top cover as its own accessory line.
 *
 * Its model carries the finish ("36G-SQ-TC-BZ") and doubles as its SKU, and its
 * price is the finish *upcharge* — a top cover's own msrp is 0. Both match what
 * AccessoryMenu produces, so a rep gets the same line whichever path added it.
 */
export function buildTopCoverLine(
  selection: BaseSelection,
  unitPrice: number,
  opts: CompanionLineOpts
): LineItem | null {
  const cover = selection.baseOptions.topCover;
  const model = topCoverLineModel(selection);
  if (!cover || !model) return null;

  const line = ({
    product_type: ProductType.Accessory,
    // Match AccessoryMenu's "<name> - <Finish> Finish" naming.
    display_name: `${cover.display_name} - ${cover.finish.display_name} Finish`,
    product_line: "",
    model,
    sku: model,
    qty: opts.qty,
    unit_price: unitPrice,
    multiplier: opts.multiplier,
    notes: "",
    item_details: { notes: [] as Note[] }
  } as unknown) as LineItem;
  stampCompanionDesign(line, opts);
  stampSetup(line, opts.setupId);
  return line;
}

/**
 * A stable signature of a base selection, for deciding whether a shared
 * design's provenance pill is still truthful on the base line.
 *
 * Deliberately not `configFingerprint`: that stringifies whole documents, and
 * the base document carries an `updated_at` that would make an untouched
 * selection look edited. Only the rep's actual choices are compared.
 */
export function baseSelectionSignature(
  selection: BaseSelection | null
): string {
  if (!selection) return "none";
  const cover = selection.baseOptions.topCover;
  return [
    selection.base.model,
    String(selection.baseFinish?.handle ?? ""),
    String(selection.baseStem ?? ""),
    selection.baseOptions.wheels ? "W" : "",
    cover ? `${cover.model}:${String(cover.finish.handle)}` : ""
  ].join("|");
}
